import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { getCurrentStore } from '@toeverything/infra';
import clsx from 'clsx';
import { Provider } from 'jotai/react';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';

import * as styles from './affine-error-fallback.css';
import { ErrorDetail } from './error-basic/error-detail';
import type { FallbackProps } from './error-basic/fallback-creator';
import { ERROR_REFLECT_KEY } from './error-basic/fallback-creator';
import { DumpInfo } from './error-basic/info-logger';
import { AnyErrorFallback } from './error-fallbacks/any-error-fallback';
import { NoPageRootFallback } from './error-fallbacks/no-page-root-fallback';
import { PageNotFoundDetail } from './error-fallbacks/page-not-found-fallback';

/**
 * Register all fallback components here.
 * If have new one just add it to the set.
 */
const fallbacks = new Set([PageNotFoundDetail, NoPageRootFallback]);

function getErrorFallbackComponent(error: any): FC<FallbackProps> {
  for (const Component of fallbacks) {
    const ErrorConstructor = Reflect.get(Component, ERROR_REFLECT_KEY);
    if (ErrorConstructor && error instanceof ErrorConstructor) {
      return Component as FC<FallbackProps>;
    }
  }
  return AnyErrorFallback;
}

function isChunkLoadError(error: unknown): boolean {
  const name =
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string'
      ? error.name
      : '';
  const message =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
      ? error.message
      : String(error);
  const text = `${name} ${message}`;
  return (
    /ChunkLoadError/i.test(text) ||
    /Loading chunk \d+ failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text)
  );
}

function chunkReloadStorageKey(): string {
  const htmlVersion =
    document.getElementById('app')?.getAttribute('data-version') ??
    BUILD_CONFIG.appVersion ??
    'unknown';
  return `streamlain:chunk-load-reload:${htmlVersion}`;
}

export interface AffineErrorFallbackProps extends FallbackProps {
  height?: number | string;
  className?: string;
}

export const AffineErrorFallback: FC<AffineErrorFallbackProps> = props => {
  const { error, resetError, height } = props;
  const Component = useMemo(() => getErrorFallbackComponent(error), [error]);

  return (
    <div className={clsx(styles.viewport, props.className)} style={{ height }}>
      <Component error={error} resetError={resetError} />
      <Provider key="JotaiProvider" store={getCurrentStore()}>
        <DumpInfo error={error} />
      </Provider>
    </div>
  );
};

export const AffineErrorComponent = () => {
  const error = useRouteError() as Error;
  const navigate = useNavigate();
  const t = useI18n();
  const [chunkReloadPending, setChunkReloadPending] = useState(false);

  // Auth errors should redirect to sign-in, not show a 500 page.
  // This happens when unauthenticated users hit pages that make GraphQL queries.
  // Check multiple signals: UserFriendlyError status, GraphQL UNAUTHENTICATED code,
  // or NestJS originalError.statusCode — different layers format auth errors differently.
  const isAuthError = (() => {
    const friendly = UserFriendlyError.fromAny(error);
    if (friendly.isStatus(401)) return true;
    if (friendly.code === 'UNAUTHENTICATED') return true;
    // NestJS GraphQL wraps UnauthorizedException in extensions.originalError
    const data = (error as any)?.extensions?.originalError;
    if (data?.statusCode === 401) return true;
    return false;
  })();

  // sl-d6mh.4.4: 2FA enforcement errors — redirect to workspace (Guard Provider handles the lock)
  const twoFactorRedirectTarget = useMemo(() => {
    const friendly = UserFriendlyError.fromAny(error);
    if (friendly.code === 'TWO_FACTOR_REQUIRED') {
      // Extract workspaceId from current URL path (/workspace/:id/...)
      const match = window.location.pathname.match(/\/workspace\/([^/]+)/);
      return match ? `/workspace/${match[1]}` : null;
    }
    const data = (error as any)?.extensions?.originalError;
    if (data?.code === 'TWO_FACTOR_REQUIRED') {
      const match = window.location.pathname.match(/\/workspace\/([^/]+)/);
      return match ? `/workspace/${match[1]}` : null;
    }
    return null;
  }, [error]);

  useEffect(() => {
    if (isAuthError) {
      navigate('/sign-in', { replace: true });
    }
  }, [isAuthError, navigate]);

  useEffect(() => {
    if (twoFactorRedirectTarget) {
      navigate(twoFactorRedirectTarget, { replace: true });
    }
  }, [twoFactorRedirectTarget, navigate]);

  useEffect(() => {
    if (!isChunkLoadError(error)) {
      return;
    }

    const key = chunkReloadStorageKey();
    try {
      if (sessionStorage.getItem(key) === '1') {
        return;
      }
      sessionStorage.setItem(key, '1');
    } catch {
      // If sessionStorage is unavailable, one reload is still safer than
      // stranding the user on a stale route chunk.
    }

    setChunkReloadPending(true);
    window.location.reload();
  }, [error]);

  const reloadPage = useCallback(() => {
    document.location.reload();
  }, []);

  // Don't render error UI while redirecting
  if (isAuthError || twoFactorRedirectTarget || chunkReloadPending) {
    return null;
  }

  return (
    <ErrorDetail
      title={t['com.streamlain.error.unexpected-error.title']()}
      resetError={reloadPage}
      buttonText={t['com.streamlain.error.reload']()}
      description={
        'message' in (error as Error) ? (error as Error).message : `${error}`
      }
      error={error as Error}
    />
  );
};
